import React from 'react'
import styled from 'styled-components'
import PricesDisplay from '../../../../components/PricesDisplay'
import { StandardColumn } from '../../../../components/StandardPage'

interface PriceItem {
  id: number
  price: string
  unitOfMeasurement: { id: number; name: string }
}

export interface Resource {
  id: number
  name: string
  code?: string
  codeId?: number | null
  category: { id: number; name: string } | null
  type: { id: number; name: string } | null
  prices?: PriceItem[]
  description: string
  totalWeight?: number
  lengthInMm?: number
  unitQuantity?: number
  stockUnit?: string
  dimension1?: number | null
  dimension2?: number | null
  material?: string | null
  format?: string | null
  fabrication?: string | null
  ncm?: string | null
  shelf?: string | null
  stockLocation?: string | null
  stockStreet?: string | null
  suppliers: { id: number; companyName: string; cnpj: string }[]
  colors?: Array<{ id: number; name: string; hex?: string; pantone?: { code?: string; hex?: string } }>
  isActivated?: boolean
}

export const resourceScreenKey = (isFashion: boolean) =>
  isFashion ? 'resource_fashion' : 'resource_steel'

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => {
    const hex = props.$hex;
    if (!hex) return '#bdbdbd';
    return hex.startsWith('#') ? hex : `#${hex}`;
  }};
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-block;
  flex-shrink: 0;
`

const ColorListCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const renderColorsCell = (resource: Resource) => {
  const colors = resource.colors || []
  if (Array.isArray(colors) && colors.length > 0) {
    return (
      <ColorListCell>
        {colors.map((c, ci) => (
          <ColorSwatchContainer key={ci}>
            <ColorSwatchCircle $hex={c.pantone?.hex || c.hex} />
            <span>
              {c.name}
              {c.pantone?.code ? ` (${c.pantone.code})` : ''}
            </span>
          </ColorSwatchContainer>
        ))}
      </ColorListCell>
    )
  }
  return <>{(resource as any).color || '—'}</>
}

const formatPricesForExport = (prices?: PriceItem[]) => {
  if (!prices || prices.length === 0) return ''
  return prices
    .map(p => `${p.price}/${p.unitOfMeasurement?.name || (p as any).UnitOfMeasurement?.name || ''}`)
    .join(', ')
}

export const buildResourceColumns = (isFashion: boolean): StandardColumn<Resource>[] => {
  const columns: StandardColumn<Resource>[] = [
    { id: 'name', label: isFashion ? 'Aviamento' : 'Insumo' },
    { id: 'category', label: 'Categoria', accessor: (r) => r.category?.name ?? '' },
    { id: 'type', label: 'Tipo', accessor: (r) => r.type?.name ?? '' },
    { id: 'code', label: 'Código' },
    {
      id: 'prices',
      label: 'Preço',
      sortable: false,
      cell: (r) => <PricesDisplay prices={(r.prices || []) as any} maxWidth={200} />,
      exportValue: (r) => formatPricesForExport(r.prices),
    },
  ]

  if (isFashion) {
    columns.push({
      id: 'color',
      label: 'Cor',
      sortable: false,
      cell: (r) => renderColorsCell(r),
      exportValue: (r) => (r.colors || []).map(c => c.name).join(', '),
    })
  }

  columns.push(
    {
      id: 'suppliers',
      label: 'Fornecedor',
      defaultHidden: true,
      accessor: (r) => r.suppliers?.map(s => s.companyName).join(', ') ?? '',
    },
    { id: 'description', label: 'Descrição', defaultHidden: true },
    { id: 'totalWeight', label: 'Peso Total', defaultHidden: true },
    { id: 'lengthInMm', label: 'Comprimento (mm)', defaultHidden: true },
    { id: 'unitQuantity', label: 'Quantidade', defaultHidden: true },
    { id: 'stockUnit', label: 'Unidade de Estoque', defaultHidden: true },
    { id: 'dimension1', label: 'Dimensão 1', defaultHidden: true },
    { id: 'dimension2', label: 'Dimensão 2', defaultHidden: true },
    { id: 'material', label: 'Material', defaultHidden: true },
    { id: 'format', label: 'Formato', defaultHidden: true },
    { id: 'fabrication', label: 'Fabricação', defaultHidden: true },
    { id: 'ncm', label: 'NCM', defaultHidden: true },
    { id: 'shelf', label: 'Prateleira', defaultHidden: true },
    { id: 'stockLocation', label: 'Localização', defaultHidden: true },
    { id: 'stockStreet', label: 'Rua do Estoque', defaultHidden: true },
  )

  return columns
}
